import { type FC, useMemo, useState } from 'react'
import DatePicker from 'react-datepicker'
import classNames from 'classnames'
import styles from './SpecificTimePicker.module.scss'
import { formatTime } from '../../bookingFormat'
import { type BookingSlot, dayKey, firstAvailableDay, groupSlotsByDay, monthBounds } from './timeSlots'
import { useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { useGetSchedulingAvailabilityQuery } from '../../../../store/scheduling/scheduling'

const MONTH_YEAR_FORMATTER = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' })

interface SpecificTimePickerProps {
  eventTypeId: string
  userId?: string
  selectedSlot: BookingSlot | null
  onSlotChange: (slot: BookingSlot | null) => void
}

const NavButton: FC<{ label: string; icon: string; disabled?: boolean; onClick: () => void }> = ({ label, icon, disabled, onClick }) => (
  <button type='button' className='btn btn-link p-0 zocks-text-gray-600' aria-label={label} disabled={disabled} onClick={onClick}>
    <i className={`bi ${icon}`} aria-hidden='true' />
  </button>
)

const startOfMonth = (day: Date): Date => new Date(day.getFullYear(), day.getMonth(), 1)

// Month calendar + time-slot list for the "Propose a specific time" sub-tab. Slots come from the
// availability API for the visible month, event type and owner; days without slots are disabled.
export const SpecificTimePicker: FC<SpecificTimePickerProps> = ({ eventTypeId, userId, selectedSlot, onSlotChange }) => {
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const [selectedDay, setSelectedDay] = useState<Date>(() => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow
  })
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => startOfMonth(selectedDay))

  const { dateFrom, dateTo } = monthBounds(visibleMonth)
  const { data: slots, isFetching } = useGetSchedulingAvailabilityQuery({ accountId, eventTypeId, userId, dateFrom, dateTo })
  const slotsByDay = useMemo(() => groupSlotsByDay(slots ?? []), [slots])
  const bookingSlots: BookingSlot[] = slotsByDay.get(dayKey(selectedDay)) ?? []

  if (slots && !isFetching && !slotsByDay.has(dayKey(selectedDay))) {
    const fallbackDay = firstAvailableDay(slots)
    if (fallbackDay) {
      setSelectedDay(fallbackDay)
    }
  }

  const isDaySelectable = (day: Date) => isFetching || !slots || slotsByDay.has(dayKey(day))

  const handleDayChange = (day: Date | null) => {
    if (day) {
      setSelectedDay(day)
      setVisibleMonth(startOfMonth(day))
      onSlotChange(null)
    }
  }

  const handleMonthChange = (month: Date) => {
    setVisibleMonth(startOfMonth(month))
    onSlotChange(null)
  }

  return (
    <div className='d-flex flex-wrap gap-3'>
      <div className='d-flex flex-column zocks-gap-0.5'>
        <div className='paragraph-small zocks-text-gray-600'>Date</div>
        <div className={styles.calendar}>
          <DatePicker
            inline
            selected={selectedDay}
            minDate={new Date()}
            calendarStartDay={1}
            formatWeekDay={(weekday) => weekday.slice(0, 3)}
            filterDate={isDaySelectable}
            onChange={handleDayChange}
            onMonthChange={handleMonthChange}
            onYearChange={handleMonthChange}
            renderCustomHeader={({ monthDate, decreaseMonth, increaseMonth, decreaseYear, increaseYear, prevMonthButtonDisabled }) => (
              <div className='d-flex align-items-center justify-content-between px-2 pb-2'>
                <div className='d-flex zocks-gap-0.5'>
                  <NavButton label='Previous year' icon='bi-chevron-double-left' onClick={decreaseYear} />
                  <NavButton label='Previous month' icon='bi-chevron-left' disabled={prevMonthButtonDisabled} onClick={decreaseMonth} />
                </div>
                <span className='bold'>{MONTH_YEAR_FORMATTER.format(monthDate)}</span>
                <div className='d-flex zocks-gap-0.5'>
                  <NavButton label='Next month' icon='bi-chevron-right' onClick={increaseMonth} />
                  <NavButton label='Next year' icon='bi-chevron-double-right' onClick={increaseYear} />
                </div>
              </div>
            )}
          />
        </div>
      </div>
      <div className='d-flex flex-column zocks-gap-0.5 flex-grow-1'>
        <div className='paragraph-small zocks-text-gray-600'>Time</div>
        {isFetching ? (
          <div className='paragraph-small zocks-text-gray-600'>Loading available times…</div>
        ) : bookingSlots.length > 0 ? (
          <div className={classNames('d-flex flex-column zocks-gap-0.5 overflow-auto', styles.slotList)}>
            {bookingSlots.map((slot) => {
              const isSelected = selectedSlot?.startAt === slot.startAt
              return (
                <button
                  key={slot.startAt}
                  type='button'
                  className={classNames(
                    'btn paragraph-small text-start rounded zocks-bg-color-white',
                    isSelected ? 'zocks-border-primary zocks-text-primary' : 'border zocks-border-color-gray-200',
                  )}
                  onClick={() => onSlotChange(slot)}
                >
                  {formatTime(new Date(slot.startAt))}
                </button>
              )
            })}
          </div>
        ) : (
          <div className='paragraph-small zocks-text-gray-600'>No available times on this day</div>
        )}
      </div>
    </div>
  )
}
